import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo, Brand } from "@/components/Logo";
import { ArrowRight, Sparkles, Crown, MailCheck } from "lucide-react";

const HERO = "https://images.pexels.com/photos/13801809/pexels-photo-13801809.jpeg";

export default function Auth() {
  const { login, register } = useAuth();
  const { token } = useParams();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [invite, setInvite] = useState(null);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("apple_error")) {
      toast.error("Apple sign-in failed or was cancelled. Please try again.");
      window.history.replaceState(null, "", "/auth");
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    api.get(`/invites/${token}`)
      .then((r) => {
        setInvite(r.data);
        setEmail(r.data.email || "");
        setMode("register");
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "This invite link is invalid or already used");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      } else if (mode === "login") {
        await login(email, password);
        toast.success("Welcome to aiBLACK");
      } else {
        await register(name, email, password, invite ? token : undefined);
        toast.success("Welcome to aiBLACK");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-4 py-3 text-white placeholder:text-[#6b7280] focus:outline-none focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] transition-colors";

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const appleSignIn = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0a0a0f]">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="aiBLACK" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/60 to-[#0a0a0f]/30" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <Logo size="text-4xl" className="mb-6" />
          <h1 className="font-display font-black text-5xl leading-none tracking-tight text-white mb-4">
            Knowledge is<br /><span className="text-[#FF5A00]">The Unfair Advantage</span>
          </h1>
          <p className="text-[#9ca3af] text-base max-w-md">
            AI answers, daily briefings, and tools built for the culture — from a Black perspective.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8"><Logo size="text-3xl" /></div>
          {invite ? (
            <div className="mb-8" data-testid="invite-heading">
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight"><Brand /> AI for Us</h2>
              <div className="text-sm uppercase tracking-[0.2em] text-[#FF5A00] font-bold mt-2">Free Pro Membership Link</div>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-3">
                <Sparkles size={14} /> {mode === "forgot" ? "Password help" : mode === "login" ? "Welcome back" : "Join the movement"}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-8">
                {mode === "forgot" ? "Reset your password" : mode === "login" ? <>Sign in to <Brand /></> : "Create your account"}
              </h2>
            </>
          )}

          {invite && (
            <div data-testid="invite-banner" className="mb-6 flex items-start gap-3 bg-[#FF5A00]/10 border border-[#FF5A00]/30 rounded-lg p-4">
              <MailCheck size={18} className="text-[#FF5A00] mt-0.5 shrink-0" />
              <div className="text-sm text-[#c7ccd6]">
                You've been invited to <Brand />. Create your account to get started.
                {invite.grant_pro && (
                  <span className="mt-1.5 inline-flex items-center gap-1.5 text-[#FF5A00] font-semibold">
                    <Crown size={14} /> Pro access is included with this invite.
                  </span>
                )}
              </div>
            </div>
          )}

          {mode === "forgot" && forgotSent ? (
            <div data-testid="forgot-sent" className="space-y-6">
              <div className="flex items-start gap-3 bg-[#FF5A00]/10 border border-[#FF5A00]/30 rounded-lg p-4">
                <MailCheck size={18} className="text-[#FF5A00] mt-0.5 shrink-0" />
                <div className="text-sm text-[#c7ccd6]">
                  If an account exists for <span className="text-white font-semibold">{email}</span>, we've sent a
                  password reset link. Check your inbox (and spam) — the link expires in 1 hour.
                </div>
              </div>
              <button data-testid="forgot-back-button" onClick={() => { setMode("login"); setForgotSent(false); }}
                className="text-[#FF5A00] font-semibold hover:text-[#FF7A33]">
                ← Back to sign in
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <input data-testid="auth-name-input" className={inputCls} placeholder="Full name"
                value={name} onChange={(e) => setName(e.target.value)} required />
            )}
            <input data-testid="auth-email-input" type="email" className={inputCls} placeholder="Email address"
              value={email} onChange={(e) => setEmail(e.target.value)} required readOnly={!!(invite && invite.email)} />
            {mode !== "forgot" && (
              <input data-testid="auth-password-input" type="password" className={inputCls} placeholder="Password"
                value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            )}

            {mode === "login" && !invite && (
              <div className="text-right">
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => { setMode("forgot"); setForgotSent(false); }}
                  className="text-sm text-[#9ca3af] hover:text-[#FF5A00] transition-colors">
                  Forgot password?
                </button>
              </div>
            )}

            <button data-testid="auth-submit-button" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3.5 hover:bg-[#FF7A33] transition-colors disabled:opacity-60">
              {loading ? "Please wait…" : mode === "forgot" ? "Send reset link" : invite ? "Join aiBLACK" : mode === "login" ? "Sign in" : "Create account"}
              {!loading && <ArrowRight size={18} />}
            </button>

            {mode === "forgot" && (
              <button type="button" data-testid="forgot-back-button" onClick={() => setMode("login")}
                className="w-full text-center text-sm text-[#9ca3af] hover:text-[#FF5A00] transition-colors">
                ← Back to sign in
              </button>
            )}
          </form>
          )}

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-6">
                <span className="h-px flex-1 bg-[#2a3040]" />
                <span className="text-xs uppercase tracking-[0.2em] text-[#6b7280]">or</span>
                <span className="h-px flex-1 bg-[#2a3040]" />
              </div>
              <button type="button" data-testid="google-signin-button" onClick={googleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-white text-[#0a0a0f] font-bold rounded-lg px-6 py-3.5 hover:bg-[#f1f1f1] transition-colors">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Continue with Google
              </button>
              <button type="button" data-testid="apple-signin-button" onClick={appleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-black text-white font-bold rounded-lg px-6 py-3.5 mt-3 border border-[#2a3040] hover:bg-[#111] transition-colors">
                <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                </svg>
                Continue with Apple
              </button>
            </>
          )}

          {!invite && mode !== "forgot" && (
            <p className="mt-6 text-sm text-[#9ca3af]">
              {mode === "login" ? <>New to <Brand />? </> : "Already have an account? "}
              <button data-testid="auth-toggle-button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-[#FF5A00] font-semibold hover:text-[#FF7A33]">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
